import "./ManualReview.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import { Field, Form, Formik } from "formik";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { SlideAdmin } from "pwapi";

import {
  fetchBarcodeUrl,
  fetchMacroUrl,
  getAdminSlide,
  updateAdminSlide,
} from "shared/actions/admin/adminSlideActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { SlideAdminWithUrls } from "shared/reducers/admin/adminSlideReducer";
import { AdminSlideListDetail } from "shared/reducers/admin/adminSlidesReducer";
import {
  specimen_source,
  has_source_attributes,
} from "shared/utils/specimenUtils";

import SlideImage from "components/admin/SlideImage";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import SelectInput from "components/customInputs/SelectInput";
import TextareaInput from "components/customInputs/TextareaInput";
import Spinner from "components/Spinner";
import { useAppDispatch } from "store/hooks";

import ManualReviewSection from "./ManualReviewSection";

type ManualReviewProps = {
  slide: SlideAdminWithUrls;
  onClose: Function;
  asyncSlideDetail?: boolean;
};

const ManualReview = (props: ManualReviewProps) => {
  const { slide, asyncSlideDetail } = props;
  const dispatch = useAppDispatch();

  const slideDetailLoading = useIsLoading(getAdminSlide, slide.id);
  const slideDetailLoaded = !!(slide as AdminSlideListDetail).detailLoaded;
  const slideDetailError = !!(slide as AdminSlideListDetail).detailError;

  useEffect(() => {
    if (
      slide.id &&
      asyncSlideDetail &&
      !slideDetailLoading &&
      !slideDetailLoaded &&
      !slideDetailError
    ) {
      dispatch(getAdminSlide(slide.id));
    }
  }, [
    slide.id,
    asyncSlideDetail,
    slideDetailLoading,
    slideDetailLoaded,
    slideDetailError,
  ]);

  if (asyncSlideDetail && slideDetailLoading) return <Spinner />;
  if (asyncSlideDetail && slideDetailError)
    return <>Error loading Slide details</>;
  if (asyncSlideDetail && !slideDetailLoaded) return null;

  return <ManualReviewInner {...props} />;
};

const ManualReviewInner = ({ slide, onClose }: ManualReviewProps) => {
  const componentSlideSaving = useRef<boolean>(false);
  const dispatch = useDispatch();

  const isNDPI = /.ndpi$/.test(slide.url_path || "");

  useEffect(() => {
    if (!isNDPI) {
      if (!slide.barcode_url) {
        dispatch(fetchBarcodeUrl(slide.id!, slide.case?.id));
      }
    } else {
      if (!slide.macro_url) {
        dispatch(fetchMacroUrl(slide.id!, slide.case?.id));
      }
    }
  }, []);

  useEffect(() => {
    if (!slide.isLoading && componentSlideSaving.current) {
      onClose();
    }
    componentSlideSaving.current = !!slide.isLoading;
  }, [slide.isLoading]);

  const submitQCInfo = (
    values: Partial<SlideAdminWithUrls>,
    status: SlideAdmin.status
  ) => {
    dispatch(
      updateAdminSlide({
        slide: { ...values, status: status },
        case: slide.case as CaseAdminDetail,
      })
    );
  };

  const getDisabledStatus = () => {
    if (
      slide.accession_prefix?.prefix === "TCO" ||
      slide.accession_prefix?.prefix === "TEST"
    ) {
      return false;
    }

    let disabled = slide.case
      ? !slide.case.accession ||
        !slide.case.ordering_doctor ||
        !slide.case.active_assignment ||
        !slide.case.patient ||
        !slide.case.patient.clinic ||
        !slide.specimen ||
        !specimen_source(slide.specimen) ||
        !slide.specimen.gross_description
      : true;

    if (!disabled) {
      disabled = isNDPI
        ? !slide.macro_url || !!slide.macro_url?.error
        : !slide.barcode_url || !!slide.barcode_url?.error;
    }

    return disabled;
  };

  const patientInsurance = slide?.case?.patient?.insurance;
  const primaryInsurance = patientInsurance?.find(
    (insur) => insur.primary_provider
  );
  const otherInsurance = patientInsurance?.filter(
    (insur) => !insur.primary_provider
  );

  return (
    <div className="ManualReview">
      {slide.isLoading && (
        <div className="spinner__container">
          <Spinner />
        </div>
      )}
      <div className="barcode">
        {isNDPI ? (
          <SlideImage
            imgSrc={slide.macro_url?.url}
            altText="slide macrocode"
            isError={slide.macro_url?.error}
            errText="Missing Macrocode"
            isFullSize
          />
        ) : (
          <SlideImage
            imgSrc={slide.barcode_url?.url}
            altText="slide barcode"
            isError={slide.barcode_url?.error}
            errText="Missing Barcode"
            isFullSize
          />
        )}
      </div>
      <div className="caseInfo__container">
        {slide.slide_review_reason && slide.status === "manual-review" && (
          <p className="reason">
            <strong>Review Reason:</strong>{" "}
            {slide.slide_review_reason.replace(/-/g, " ")}
          </p>
        )}
        <div className="details__container">
          <ManualReviewSection
            label="Case Details"
            error={!slide.case}
            errorMessage="Missing Case"
          >
            <p>{slide.case?.accession}</p>
            {slide.case && (
              <ManualReviewSection
                label="Specimen Details"
                error={!slide.specimen}
                errorMessage="Missing Specimen"
              >
                <p
                  className={classnames({
                    section__error: !slide.specimen?.name,
                  })}
                >
                  {!slide.specimen?.name && (
                    <>
                      <FontAwesomeIcon color="red" icon="exclamation" />
                      &nbsp;
                    </>
                  )}
                  Specimen: {slide.specimen?.name}
                </p>
                <p
                  className={classnames({
                    section__error:
                      !slide.block_obj?.name && !slide.specimen?.name,
                  })}
                >
                  {!slide.block_obj?.name && !slide.specimen?.name && (
                    <>
                      <FontAwesomeIcon color="red" icon="exclamation" />
                      &nbsp;
                    </>
                  )}
                  Block: {slide.specimen?.name || ""}
                  {slide.block_obj?.name || ""}
                </p>
                <div className="specimen__grid">
                  {slide.specimen && has_source_attributes(slide.specimen) ? (
                    <FontAwesomeIcon color="green" icon="check" />
                  ) : (
                    <FontAwesomeIcon color="red" icon="exclamation" />
                  )}
                  <span>Source</span>

                  {slide.specimen?.clinical_history ? (
                    <FontAwesomeIcon color="green" icon="check" />
                  ) : (
                    <FontAwesomeIcon color="red" icon="exclamation" />
                  )}
                  <span>Clinical History</span>

                  {slide.specimen?.gross_description ? (
                    <FontAwesomeIcon color="green" icon="check" />
                  ) : (
                    <FontAwesomeIcon color="red" icon="exclamation" />
                  )}
                  <span>Gross Description</span>
                </div>
              </ManualReviewSection>
            )}
          </ManualReviewSection>
          {slide.case ? (
            <>
              <div className="section__container section__container--gap">
                <ManualReviewSection
                  label="Patient"
                  error={!slide.case.patient}
                  errorMessage="Missing Patient"
                >
                  <>
                    <p>
                      {slide.case?.patient?.last_name},{" "}
                      {slide.case?.patient?.first_name}
                    </p>
                    <p>
                      {slide.case?.patient?.sex} -{" "}
                      <time>{slide.case?.patient?.date_of_birth}</time>
                    </p>
                  </>
                </ManualReviewSection>
                <ManualReviewSection
                  label="Patient Clinic"
                  error={!slide.case.patient?.clinic}
                  errorMessage="Missing Clinic"
                >
                  <p>{slide.case.patient?.clinic?.name}</p>
                </ManualReviewSection>
                {patientInsurance?.length ? (
                  <ManualReviewSection
                    label="Patient Insurance"
                    errorMessage=""
                  >
                    <>
                      {primaryInsurance && (
                        <>
                          <h3>Primary:</h3>
                          <span>{primaryInsurance.insurance_company_name}</span>
                        </>
                      )}
                      {otherInsurance?.length ? (
                        <>
                          <h3>Additional:</h3>
                          {otherInsurance.map((insur, index) => (
                            <span key={`${insur.id}-${index}`}>
                              {insur.insurance_company_name}
                            </span>
                          ))}
                        </>
                      ) : undefined}
                    </>
                  </ManualReviewSection>
                ) : undefined}
              </div>
              <ManualReviewSection
                label="Active Assignment"
                error={!slide.case.active_assignment}
                errorMessage="Missing Active Assignment"
              >
                <p>
                  {slide.case?.active_assignment?.last_name},{" "}
                  {slide.case?.active_assignment?.first_name}
                </p>
              </ManualReviewSection>
              <ManualReviewSection
                label="Ordering Provider"
                error={!slide.case.ordering_doctor}
                errorMessage="Missing Ordering Provider"
              >
                <p>
                  {slide.case?.ordering_doctor?.last_name},{" "}
                  {slide.case?.ordering_doctor?.first_name}
                </p>
              </ManualReviewSection>
            </>
          ) : null}
        </div>
      </div>
      {slide.status === "manual-review" ? (
        <Formik
          initialValues={{
            id: slide.id,
            qc_status: slide.qc_status,
            qc_notes: slide.qc_notes,
          }}
          onSubmit={() => {}}
        >
          {(formikProps: any) => {
            const { values } = formikProps;
            return (
              <Form className="ManualReview__status">
                <div>
                  <Field name="qc_status">
                    {({ field }: any) => (
                      <SelectInput
                        label="QC Status"
                        id="qc_status"
                        options={[
                          ...Object.values(SlideAdmin.qc_status).map((p) => ({
                            label: p,
                            value: p,
                          })),
                        ]}
                        {...field}
                        value={field.value || ""}
                      />
                    )}
                  </Field>
                  <Field name="qc_notes">
                    {({ field }: any) => (
                      <TextareaInput
                        label="QC Notes"
                        id="qc_notes"
                        rows={4}
                        cols={40}
                        {...field}
                      />
                    )}
                  </Field>
                </div>
                <div className="actions__container">
                  <PathologyWatchButton
                    color="yellow"
                    title="submit manual review with qc information"
                    onClick={() =>
                      submitQCInfo(values, SlideAdmin.status.ON_HOLD)
                    }
                  >
                    Move to On Hold
                  </PathologyWatchButton>

                  <PathologyWatchButton
                    onClick={() =>
                      submitQCInfo(values, SlideAdmin.status.SUCCESS)
                    }
                    color="green"
                    disabled={getDisabledStatus()}
                  >
                    Move to Success
                  </PathologyWatchButton>
                </div>
              </Form>
            );
          }}
        </Formik>
      ) : (
        <div className="actions__container">
          <PathologyWatchButton
            onClick={() => {
              onClose();
            }}
            title="close"
          >
            Close
          </PathologyWatchButton>
        </div>
      )}
    </div>
  );
};

export default ManualReview;
